/**
 * Admissions/CRM API (issue #9) — the applicant kanban + convert-to-student.
 *
 * Typed wrappers over the shared `api` client (admin-only server-side, via
 * `verify_admin` + RLS — this client makes no authority claim). Mirrors
 * students.ts's shape.
 */
import { api } from "@/lib/api/client";
import { commitOptions, retryOptions, type BulkResult } from "./commitSeams";

export type ApplicantStage =
  | "Applied"
  | "Under Review"
  | "Interview Invited"
  | "Decision Pending"
  | "Admitted"
  | "Waitlisted"
  | "Denied";

export type ApplicantIs = "parent" | "student";
export type DocumentStatus = "received" | "pending" | "waived";

export interface ApplicantListItem {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  program_id: string | null;
  program_name: string | null;
  round: string;
  stage: ApplicantStage;
  priority: string;
  applied_date: string;
  applicant_is: ApplicantIs | null;
  converted_student_id: string | null;
  updated_at: string;
  doc_total: number;
  doc_complete: boolean;
}

export interface ApplicantProfile {
  id: string;
  tenant_id: string;
  name: string;
  email: string;
  phone: string | null;
  program_id: string | null;
  program_name: string | null;
  round: string;
  stage: ApplicantStage;
  priority: string;
  applied_date: string;
  applicant_is: ApplicantIs | null;
  applicant_message: string | null;
  internal_note: string | null;
  submission_source: string | null;
  // Tenant-configured intake fields (migration 054). Null on every applicant
  // whose tenant does not collect the field and on every row that predates it,
  // so the sheet renders what was actually submitted rather than empty labels.
  date_of_birth: string | null;
  school_name: string | null;
  grade_level: string | null;
  major: string | null;
  mother_name: string | null;
  mother_phone: string | null;
  mother_address: string | null;
  father_name: string | null;
  father_phone: string | null;
  father_address: string | null;
  confirmation_email_state:
    | "pending"
    | "sending"
    | "sent"
    | "failed"
    | "not_configured"
    | "unknown";
  confirmation_email_attempts: number;
  confirmation_email_last_attempt_at: string | null;
  confirmation_email_sent_at: string | null;
  confirmation_email_provider_ref: string | null;
  confirmation_email_last_error: string | null;
  converted_student_id: string | null;
  converted_person_id: string | null;
  stage_updated_by_name: string | null;
  stage_updated_at: string | null;
  stage_updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ApplicationDocument {
  id: string;
  doc_name: string;
  status: DocumentStatus;
  created_at: string;
  updated_at: string;
}

export interface ApplicantDetail {
  applicant: ApplicantProfile;
  documents: ApplicationDocument[];
}

export interface ApplicantFilters {
  program_id?: string;
  stage?: string;
  q?: string;
}

/** stage/program_id are rejected (409) by the server once converted_student_id
 * is set — priority/internal_note stay editable post-conversion. */
export interface PatchApplicantBody {
  expected_updated_at: string;
  stage?: ApplicantStage;
  program_id?: string | null;
  priority?: string;
  internal_note?: string | null;
}

/** Immutable outcome of this command. Current personal facts come from GET. */
export interface ApplicantCommit {
  state: "confirmed";
  applicant_id: string;
  updated_at: string;
}

export interface ConvertResult {
  person_id: string;
  converted_student_id: string;
  sid: string;
}

export interface ConfirmationRetryResult {
  state: ApplicantProfile["confirmation_email_state"];
  detail: ApplicantDetail;
  attempt_count: number;
  provider_ref: string | null;
}

/** The largest set `POST /applicants/bulk-stage` accepts; the 51st id is a 422. */
export const BULK_STAGE_MAX = 50;

/** Why one requested id did not move. `not_found` is deliberately the single
 * answer for an id that never existed, one that belongs to another tenant, and
 * one that is not a uuid at all — the server must not let those be told apart. */
export interface SkippedApplicant {
  id: string;
  reason: "not_found" | "already_converted" | "changed";
}

/** Every requested id resolves to exactly one outcome, in request order. */
export interface BulkStageResult {
  bulk: BulkResult;
  updated_ids: string[];
  skipped: SkippedApplicant[];
}

export function getApplicants(filters: ApplicantFilters = {}): Promise<ApplicantListItem[]> {
  const params = new URLSearchParams();
  if (filters.program_id) params.set("program_id", filters.program_id);
  if (filters.stage) params.set("stage", filters.stage);
  if (filters.q) params.set("q", filters.q);
  const qs = params.toString();
  return api.get<ApplicantListItem[]>(`/applicants${qs ? `?${qs}` : ""}`);
}

export function getApplicant(id: string): Promise<ApplicantDetail> {
  return api.get<ApplicantDetail>(`/applicants/${id}`);
}

export function patchApplicant(id: string, body: PatchApplicantBody, key: string): Promise<ApplicantCommit> {
  return api.patch<ApplicantCommit>(`/applicants/${id}`, body, commitOptions(key));
}

export function convertApplicant(id: string, cohortId: string | null, version: string, key: string): Promise<ConvertResult> {
  return api.post<ConvertResult>(`/applicants/${id}/convert`, { cohort_id: cohortId, expected_updated_at: version }, commitOptions(key));
}

export function retryApplicantConfirmation(id: string, version: string, key: string): Promise<ConfirmationRetryResult> {
  return api.post<ConfirmationRetryResult>(`/applicants/${id}/confirmation/retry`, { expected_updated_at: version }, commitOptions(key));
}

/** Advance a checked set of files to one stage. Partial success is the ordinary
 * result, not an error: the response names every id that did not move and why. */
export function bulkStageApplicants(
  ids: string[],
  stage: ApplicantStage,
  versions: Record<string, string>,
  key: string,
  retry = false,
): Promise<BulkStageResult> {
  return api.post<BulkStageResult>("/applicants/bulk-stage", {
    applicant_ids: ids,
    stage,
    expected_updated_at: versions,
  }, retry ? retryOptions(key) : commitOptions(key));
}

export function patchApplicantDocument(
  applicantId: string,
  docId: string,
  status: DocumentStatus,
  version: string,
  key: string,
): Promise<ApplicationDocument> {
  return api.patch<ApplicationDocument>(`/applicants/${applicantId}/documents/${docId}`, { status, expected_updated_at: version }, commitOptions(key));
}

export function deleteApplicant(id: string, version: string, key: string): Promise<void> {
  return api.delete<void>(`/applicants/${id}`, { expected_updated_at: version }, commitOptions(key));
}

/** One register read owns rows, head figures and the offered file. */
export interface ApplicantRegister {
  applicants: ApplicantListItem[];
  offer: string | null;
  ranks: { state: string; value?: { candidates: { id: string; eligible: boolean; blocked: boolean; read: string; rank: number | null }[]; lead: string | null } };
}
export function getApplicantRegister(): Promise<ApplicantRegister> {
  return api.get<ApplicantRegister>("/applicants/register");
}
